import React, { useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { removeClientFromLocal } from '../../../../../../../../crud/clients_api/local_clients';
import { ClientLocal } from '../../../../types/client.types';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';

import './RemoveClient.scss';

interface RemoveClientProps {
  show: boolean;
  handleClose: () => void;
  onSuccess: () => void;
  localId: number;
  client: ClientLocal;
}

const RemoveClient: React.FC<RemoveClientProps> = ({
  show,
  handleClose,
  onSuccess,
  localId,
  client,
}) => {
  const [isRemoving, setIsRemoving] = useState(false);

  const getFullName = () => {
    const parts = [client.username, client.middle_name, client.last_name].filter(Boolean);
    return parts.join(' ') || 'este cliente';
  };

  const handleRemoveClient = async () => {
    setIsRemoving(true);
    try {
      await removeClientFromLocal(localId, client.client_id);
      toast.success(`Cliente ${getFullName()} eliminado exitosamente`);
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('Error removing client:', error);
      toast.error(error?.message || 'Error al eliminar el cliente');
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="remove-client-modal">
      <Modal.Header>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">Eliminar Cliente</h2>
        </div>
        <button className="modal-close-button" onClick={handleClose}>
          <FaTimes />
        </button>
      </Modal.Header>

      <Modal.Body>
        <div className="warning-banner">
          <FaExclamationTriangle className="warning-icon" />
          <div className="warning-content">
            <h4>¡Advertencia! Esta acción es irreversible</h4>
            <p>
              Al eliminar a <strong>{getFullName()}</strong> de tu lista de clientes, se eliminarán permanentemente:
            </p>
            <ul>
              <li>Todo el historial de visitas ({client.visits} visitas)</li>
              <li>Estadísticas de gasto total (${client.total_spent.toFixed(2)})</li>
              <li>Información de membresía actual</li>
              <li>Relación completa con tu local</li>
            </ul>
            <p className="warning-note">
              Si vuelves a agregar a este cliente en el futuro, comenzará desde cero con valores predeterminados.
              Esta acción puede afectar significativamente la relación con tu cliente.
            </p>
          </div>
        </div>

        <div className="client-info-summary">
          <h5>Información del Cliente</h5>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">Nombre:</span>
              <span className="value">{getFullName()}</span>
            </div>
            {client.email && (
              <div className="info-item">
                <span className="label">Email:</span>
                <span className="value">{client.email}</span>
              </div>
            )}
            {client.phone_number && (
              <div className="info-item">
                <span className="label">Teléfono:</span>
                <span className="value">{client.phone_number}</span>
              </div>
            )}
            <div className="info-item">
              <span className="label">Membresía:</span>
              <span className="value">
                {client.membership ? client.membership.membership_name : 'Sin membresía'}
              </span>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isRemoving}
          >
            Cancelar
          </Button>
          <Button
            className="form-button-delete"
            onClick={handleRemoveClient}
            disabled={isRemoving}
          >
            {isRemoving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Eliminando...
              </>
            ) : (
              'Confirmar Eliminación'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RemoveClient;
